import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, euro, formatApiError, shortDate, TX_LABELS } from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft, Printer, LockKey, Warning } from "@phosphor-icons/react";
import { BRAND } from "../config/brand";
import { useBranding } from "../lib/branding";

export default function ClosureDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [c, setC] = useState(null);
    const [err, setErr] = useState(null);
    const { company, logo_b64 } = useBranding();

    useEffect(() => {
        api
            .get(`/closures/${id}`)
            .then((r) => setC(r.data))
            .catch((e) => setErr(formatApiError(e.response?.data?.detail) || "Errore"));
    }, [id]);

    if (err)
        return (
            <div className="p-12">
                <div className="overline mb-4">Errore</div>
                <div className="text-red-600 mb-6">{err}</div>
                <Link to="/closures" className="btn-secondary inline-flex items-center gap-2">
                    <ArrowLeft size={14} weight="bold" /> Torna alle chiusure
                </Link>
            </div>
        );
    if (!c) return <div className="overline p-12">Caricamento…</div>;

    const cashTotal = (c.wallets || [])
        .filter((w) => ["cassa", "cassaforte"].includes(w.type))
        .reduce((s, w) => s + (w.balance || 0), 0);
    const bankTotal = (c.wallets || [])
        .filter((w) => w.type === "banca")
        .reduce((s, w) => s + (w.balance || 0), 0);
    const utiliTotal = (c.wallets || [])
        .filter((w) => w.type === "utili_accantonati")
        .reduce((s, w) => s + (w.balance || 0), 0);
    const providersOutstanding = (c.providers || []).reduce(
        (s, p) => s + (p.outstanding_credit || 0),
        0,
    );
    const fidoTotal = (c.providers || []).reduce((s, p) => s + (p.fido_limit || 0), 0);
    const marginPct = c.accrediti > 0 ? (c.pnl / c.accrediti) * 100 : null;

    const doPrint = () => {
        setTimeout(() => window.print(), 100);
    };

    return (
        <div data-testid="closure-detail-page">
            <div className="flex items-end justify-between mb-8 flex-wrap gap-4 no-print">
                <div>
                    <button
                        onClick={() => navigate("/closures")}
                        className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-zinc-500 hover:text-ink mb-3"
                        data-testid="back-to-closures"
                    >
                        <ArrowLeft size={12} weight="bold" /> Chiusure
                    </button>
                    <div className="overline mb-2 flex items-center gap-2">
                        <LockKey size={11} weight="bold" /> Snapshot del mese chiuso
                    </div>
                    <h1 className="text-4xl md:text-5xl font-black tracking-tighter">
                        {c.year_month}
                    </h1>
                    <div className="text-sm text-zinc-600 mt-1">
                        Chiuso da <strong>{c.closed_by}</strong> il {shortDate(c.closed_at)}
                    </div>
                </div>
                <button
                    onClick={doPrint}
                    className="btn-primary flex items-center gap-2"
                    data-testid="print-detail-btn"
                >
                    <Printer size={14} weight="bold" /> Stampa report
                </button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 grid-room bg-white mb-8 no-print">
                <div className="p-6 grid-cell">
                    <div className="overline mb-2">Entrate</div>
                    <div
                        className="text-2xl font-black tabular-nums"
                        style={{ color: "#00C853" }}
                    >
                        {euro(c.entrate)}
                    </div>
                    <div className="text-xs text-zinc-500 mt-1 tabular-nums">
                        di cui accrediti {euro(c.accrediti)}
                    </div>
                </div>
                <div className="p-6 grid-cell">
                    <div className="overline mb-2">Uscite</div>
                    <div
                        className="text-2xl font-black tabular-nums"
                        style={{ color: "#FF2A00" }}
                    >
                        {euro(c.uscite)}
                    </div>
                </div>
                <div className="p-6 grid-cell">
                    <div className="overline mb-2">P&L</div>
                    <div
                        className="text-2xl font-black tabular-nums"
                        style={{ color: c.pnl >= 0 ? "#00C853" : "#FF2A00" }}
                    >
                        {euro(c.pnl)}
                    </div>
                    {marginPct !== null && (
                        <div className="text-xs text-zinc-500 mt-1 tabular-nums">
                            margine {marginPct.toFixed(1)}%
                        </div>
                    )}
                </div>
                <div className="p-6 grid-cell">
                    <div className="overline mb-2">Accantonato</div>
                    <div className="text-2xl font-black tabular-nums">
                        {euro(c.accantonato || 0)}
                    </div>
                    <div className="text-xs text-zinc-500 mt-1 tabular-nums">
                        Utili tot. {euro(utiliTotal)}
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8 no-print">
                <div className="bg-white border border-zinc-200">
                    <div className="px-6 py-4 border-b border-zinc-200 overline">
                        Saldi Wallet (snapshot)
                    </div>
                    <table className="ft-table">
                        <thead>
                            <tr>
                                <th>Nome</th>
                                <th>Tipo</th>
                                <th className="text-right">Saldo</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(c.wallets || []).map((w) => (
                                <tr key={w.id}>
                                    <td className="font-bold">{w.name}</td>
                                    <td className="text-xs uppercase text-zinc-500">{w.type}</td>
                                    <td className="text-right tabular-nums font-bold">
                                        {euro(w.balance)}
                                    </td>
                                </tr>
                            ))}
                            <tr className="bg-zinc-50">
                                <td colSpan={2} className="font-bold">
                                    Totale cassa+cassaforte
                                </td>
                                <td className="text-right tabular-nums font-bold">
                                    {euro(cashTotal)}
                                </td>
                            </tr>
                            <tr>
                                <td colSpan={2} className="font-bold text-zinc-600">
                                    Totale banca
                                </td>
                                <td className="text-right tabular-nums">{euro(bankTotal)}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div className="bg-white border border-zinc-200">
                    <div className="px-6 py-4 border-b border-zinc-200 overline">
                        Fornitori (snapshot)
                    </div>
                    <table className="ft-table">
                        <thead>
                            <tr>
                                <th>Nome</th>
                                <th className="text-right">Fido</th>
                                <th className="text-right">Usato</th>
                                <th className="text-right">Residuo</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(c.providers || []).map((p) => (
                                <tr key={p.id}>
                                    <td className="font-bold">{p.name}</td>
                                    <td className="text-right tabular-nums">
                                        {euro(p.fido_limit)}
                                    </td>
                                    <td className="text-right tabular-nums">
                                        {euro(p.outstanding_credit)}
                                    </td>
                                    <td className="text-right tabular-nums">
                                        {euro((p.fido_limit || 0) - (p.outstanding_credit || 0))}
                                    </td>
                                </tr>
                            ))}
                            <tr className="bg-zinc-50">
                                <td className="font-bold">Totale</td>
                                <td className="text-right tabular-nums font-bold">
                                    {euro(fidoTotal)}
                                </td>
                                <td className="text-right tabular-nums font-bold">
                                    {euro(providersOutstanding)}
                                </td>
                                <td className="text-right tabular-nums font-bold">
                                    {euro(fidoTotal - providersOutstanding)}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8 no-print">
                <div className="bg-white border border-zinc-200">
                    <div className="px-6 py-4 border-b border-zinc-200 overline">
                        Spese per Categoria
                    </div>
                    {(c.expenses_by_category || []).length === 0 ? (
                        <div className="p-10 text-center text-zinc-500 text-sm">
                            Nessuna spesa nel mese
                        </div>
                    ) : (
                        <table className="ft-table">
                            <thead>
                                <tr>
                                    <th>Categoria</th>
                                    <th className="text-right">N.</th>
                                    <th className="text-right">Totale</th>
                                    <th className="text-right">%</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(c.expenses_by_category || []).map((r) => {
                                    const pct = c.uscite > 0 ? (r.total / c.uscite) * 100 : 0;
                                    return (
                                        <tr key={r.category}>
                                            <td className="font-bold">{r.category}</td>
                                            <td className="text-right tabular-nums text-zinc-500">
                                                {r.count}
                                            </td>
                                            <td className="text-right tabular-nums font-bold">
                                                {euro(r.total)}
                                            </td>
                                            <td className="text-right tabular-nums text-zinc-500 text-xs">
                                                {pct.toFixed(1)}%
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>

                <div className="bg-white border border-zinc-200">
                    <div className="px-6 py-4 border-b border-zinc-200 overline">
                        Accrediti per Fornitore
                    </div>
                    {(c.accrediti_by_provider || []).length === 0 ? (
                        <div className="p-10 text-center text-zinc-500 text-sm">
                            Nessun accredito nel mese
                        </div>
                    ) : (
                        <table className="ft-table">
                            <thead>
                                <tr>
                                    <th>Fornitore</th>
                                    <th className="text-right">N.</th>
                                    <th className="text-right">Totale</th>
                                    <th className="text-right">%</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(c.accrediti_by_provider || []).map((r) => {
                                    const pct =
                                        c.accrediti > 0 ? (r.total / c.accrediti) * 100 : 0;
                                    return (
                                        <tr key={r.provider_id || r.provider_name}>
                                            <td className="font-bold">{r.provider_name}</td>
                                            <td className="text-right tabular-nums text-zinc-500">
                                                {r.count}
                                            </td>
                                            <td className="text-right tabular-nums font-bold">
                                                {euro(r.total)}
                                            </td>
                                            <td className="text-right tabular-nums text-zinc-500 text-xs">
                                                {pct.toFixed(1)}%
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            <div className="bg-white border border-zinc-200 mb-8 no-print">
                <div className="px-6 py-4 border-b border-zinc-200 overline">
                    Transazioni del mese · {(c.transactions || []).length}
                </div>
                <div className="max-h-96 overflow-y-auto">
                    <table className="ft-table">
                        <thead>
                            <tr>
                                <th>Data</th>
                                <th>Tipo</th>
                                <th>Descrizione</th>
                                <th>Categoria</th>
                                <th className="text-right">Importo</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(c.transactions || []).map((t) => (
                                <tr key={t.id}>
                                    <td className="mono text-xs">{shortDate(t.occurred_at)}</td>
                                    <td className="text-xs uppercase font-bold">
                                        {TX_LABELS[t.type] || t.type}
                                    </td>
                                    <td className="text-zinc-600 text-xs">
                                        {t.description || "—"}
                                    </td>
                                    <td className="text-xs text-zinc-500">
                                        {t.category || "—"}
                                    </td>
                                    <td className="text-right tabular-nums font-bold">
                                        {euro(t.amount)}
                                    </td>
                                </tr>
                            ))}
                            {(c.transactions || []).length === 0 && (
                                <tr>
                                    <td colSpan={5} className="text-center text-zinc-500 py-6">
                                        Nessuna transazione
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Print-only section */}
            <div id="print-area" className="print-only">
                <div className="print-header">
                    <div className="print-brandbar">
                        {logo_b64 && (
                            <img
                                src={logo_b64}
                                alt={company?.name || "Logo azienda"}
                                className="print-logo"
                            />
                        )}
                        <div className="print-brand-text">
                            {company?.name && (
                                <div className="print-company">{company.name}</div>
                            )}
                            {company?.vat && (
                                <div className="print-vat">P.IVA {company.vat}</div>
                            )}
                        </div>
                        <div className="print-platform">Powered by {BRAND.name}</div>
                    </div>
                    <div className="overline">Resoconto Mensile</div>
                    <h2>{c.year_month}</h2>
                    <div className="print-meta">
                        Chiuso da <strong>{c.closed_by}</strong> il {shortDate(c.closed_at)}
                    </div>
                </div>

                <h3>Riepilogo economico</h3>
                <table className="print-table">
                    <tbody>
                        <tr>
                            <td>Entrate (incl. accrediti)</td>
                            <td className="r">{euro(c.entrate)}</td>
                        </tr>
                        <tr>
                            <td>· di cui accrediti provider</td>
                            <td className="r">{euro(c.accrediti)}</td>
                        </tr>
                        <tr>
                            <td>Uscite</td>
                            <td className="r">{euro(c.uscite)}</td>
                        </tr>
                        <tr className="pnl">
                            <td>P&L del mese</td>
                            <td className="r">{euro(c.pnl)}</td>
                        </tr>
                        {marginPct !== null && (
                            <tr>
                                <td>Margine su accrediti</td>
                                <td className="r">{marginPct.toFixed(2)}%</td>
                            </tr>
                        )}
                        <tr>
                            <td>Accantonato in Utili</td>
                            <td className="r">{euro(c.accantonato || 0)}</td>
                        </tr>
                        <tr>
                            <td>Sospesi clienti alla chiusura</td>
                            <td className="r">{euro(c.clients_outstanding)}</td>
                        </tr>
                    </tbody>
                </table>

                <h3>Spese per Categoria</h3>
                {(c.expenses_by_category || []).length === 0 ? (
                    <div className="small">Nessuna spesa nel mese</div>
                ) : (
                    <table className="print-table">
                        <thead>
                            <tr>
                                <th>Categoria</th>
                                <th className="r">N.</th>
                                <th className="r">Totale</th>
                                <th className="r">% sulle uscite</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(c.expenses_by_category || []).map((r) => {
                                const pct = c.uscite > 0 ? (r.total / c.uscite) * 100 : 0;
                                return (
                                    <tr key={r.category}>
                                        <td>{r.category}</td>
                                        <td className="r">{r.count}</td>
                                        <td className="r">{euro(r.total)}</td>
                                        <td className="r">{pct.toFixed(1)}%</td>
                                    </tr>
                                );
                            })}
                            <tr className="pnl">
                                <td>Totale uscite</td>
                                <td className="r"></td>
                                <td className="r">{euro(c.uscite)}</td>
                                <td className="r">100.0%</td>
                            </tr>
                        </tbody>
                    </table>
                )}

                <h3>Accrediti per Fornitore</h3>
                {(c.accrediti_by_provider || []).length === 0 ? (
                    <div className="small">Nessun accredito nel mese</div>
                ) : (
                    <table className="print-table">
                        <thead>
                            <tr>
                                <th>Fornitore</th>
                                <th className="r">N.</th>
                                <th className="r">Totale</th>
                                <th className="r">% sugli accrediti</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(c.accrediti_by_provider || []).map((r) => {
                                const pct =
                                    c.accrediti > 0 ? (r.total / c.accrediti) * 100 : 0;
                                return (
                                    <tr key={r.provider_id || r.provider_name}>
                                        <td>{r.provider_name}</td>
                                        <td className="r">{r.count}</td>
                                        <td className="r">{euro(r.total)}</td>
                                        <td className="r">{pct.toFixed(1)}%</td>
                                    </tr>
                                );
                            })}
                            <tr className="pnl">
                                <td>Totale accrediti</td>
                                <td className="r"></td>
                                <td className="r">{euro(c.accrediti)}</td>
                                <td className="r">100.0%</td>
                            </tr>
                        </tbody>
                    </table>
                )}

                <h3>Saldi Wallet (snapshot)</h3>
                <table className="print-table">
                    <thead>
                        <tr>
                            <th>Nome</th>
                            <th>Tipo</th>
                            <th className="r">Saldo</th>
                        </tr>
                    </thead>
                    <tbody>
                        {(c.wallets || []).map((w) => (
                            <tr key={w.id}>
                                <td>{w.name}</td>
                                <td className="small">{w.type}</td>
                                <td className="r">{euro(w.balance)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                <h3>Fornitori (snapshot fido)</h3>
                <table className="print-table">
                    <thead>
                        <tr>
                            <th>Nome</th>
                            <th className="r">Fido</th>
                            <th className="r">Outstanding</th>
                            <th className="r">Residuo</th>
                        </tr>
                    </thead>
                    <tbody>
                        {(c.providers || []).map((p) => (
                            <tr key={p.id}>
                                <td>{p.name}</td>
                                <td className="r">{euro(p.fido_limit)}</td>
                                <td className="r">{euro(p.outstanding_credit)}</td>
                                <td className="r">
                                    {euro((p.fido_limit || 0) - (p.outstanding_credit || 0))}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                <div className="print-footer">
                    Generato da {BRAND.name} · {new Date().toLocaleString("it-IT")}
                </div>
            </div>
        </div>
    );
}
